import React, { useMemo, useState } from 'react';
import { agentAPI } from '../../api/agent.api';
import type { AgentStatusData, AgentStats } from '../../types/agent.types';

interface AgentHeaderProps {
  status: AgentStatusData;
  stats: AgentStats;
  pendingCount: number;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'a l\'instant';
  if (diffMin < 60) return `il y a ${diffMin}min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `il y a ${diffHours}h`;
  return date.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' });
}

export const AgentHeader: React.FC<AgentHeaderProps> = ({
  status,
  stats,
  pendingCount,
}) => {
  const [isPausing, setIsPausing] = useState(false);
  const [isKilling, setIsKilling] = useState(false);

  const { statusState, statusLabel } = useMemo(() => {
    const lastAnalysis = status.last_analysis_at ? new Date(status.last_analysis_at) : null;
    const now = new Date();
    const diffMinutes = lastAnalysis ? Math.floor((now.getTime() - lastAnalysis.getTime()) / 60000) : Infinity;

    let state: 'running' | 'paused' | 'stopped';
    let label: string;

    if (diffMinutes < 8) {
      state = 'running';
      label = 'En cours';
    } else if (diffMinutes < 30) {
      state = 'paused';
      label = 'Ralenti';
    } else {
      state = 'stopped';
      label = 'Arrete';
    }

    return { statusState: state, statusLabel: label };
  }, [status.last_analysis_at]);

  const handlePauseResume = async () => {
    setIsPausing(true);
    try {
      await agentAPI.togglePause();
    } catch {
      // silently fail
    } finally {
      setIsPausing(false);
    }
  };

  const handleKill = async () => {
    if (!window.confirm('KILL SWITCH: Desactiver TOUTES les situations? Lea arretera immediatement toutes les actions.')) return;
    setIsKilling(true);
    try {
      const response = await agentAPI.killAll();
      const msg = response?.data
        ? `Kill switch active: ${response.data.situations_disabled || 0} desactivees, ${response.data.plans_cancelled || 0} plans annules`
        : 'Kill switch active';
      alert(msg);
    } catch {
      alert('Erreur lors de l\'activation du kill switch');
    } finally {
      setIsKilling(false);
    }
  };

  return (
    <div className="lea-header">
      <div className="lea-header-top">
        <div className="lea-header-left">
          <h1 className="lea-header-title">Agent Lea</h1>
          <span className={`lea-header-status lea-header-status--${statusState}`}>
            <span className={`lea-status-dot lea-status-dot--${statusState}`} />
            {statusLabel}
          </span>
        </div>

        <div className="lea-header-right">
          <button
            className="lea-header-btn"
            onClick={handlePauseResume}
            disabled={isPausing}
          >
            {statusState === 'running' ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
            {isPausing ? '...' : statusState === 'running' ? 'Pause' : 'Reprendre'}
          </button>

          <button
            className="lea-header-btn lea-header-btn--kill"
            onClick={handleKill}
            disabled={isKilling}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
            {isKilling ? '...' : 'KILL'}
          </button>
        </div>
      </div>

      <div className="lea-header-stats">
        <div className="lea-stat-item">
          <span className="lea-stat-value">{pendingCount}</span>
          <span className="lea-stat-label">en attente</span>
        </div>
        <div className="lea-stat-separator" />
        <div className="lea-stat-item">
          <span className="lea-stat-value">{stats.validated_today}</span>
          <span className="lea-stat-label">valides</span>
        </div>
        <div className="lea-stat-separator" />
        <div className="lea-stat-item">
          <span className="lea-stat-value">{stats.cancelled_today}</span>
          <span className="lea-stat-label">refuses</span>
        </div>
        <div className="lea-stat-separator" />
        <div className="lea-stat-item">
          <span className="lea-stat-value">{stats.executed_month}</span>
          <span className="lea-stat-label">executes (mois)</span>
        </div>
        <div className="lea-stat-separator" />
        <span className="lea-stat-time">
          Dernier cycle: {formatRelativeTime(status.last_analysis_at)}
        </span>
      </div>
    </div>
  );
};
